<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { formatHms } from '@atlasguess/games';

  interface Props {
    /**
     * Wall-clock timestamp (performance.now()) when the quiz started.
     * Null while not yet started OR when the quiz has finished — we
     * stop the ticking display in both cases.
     */
    startedAtMs: number | null;
    /**
     * Frozen display value when the quiz is finished — instead of
     * computing live elapsed, the HUD shows this final time. Hud
     * displays this whenever provided (regardless of startedAtMs).
     */
    frozenElapsedMs?: number | null;
    /**
     * Progress: how many countries resolved out of the total pool.
     * Drives the "12/53" counter so the player has a sense of pace.
     */
    resolved: number;
    total: number;
    onRestart: () => void;
  }

  let { startedAtMs, frozenElapsedMs, resolved, total, onRestart }: Props = $props();

  // Tick at 1Hz — sub-second resolution doesn't matter for an HH:MM:SS
  // display and a 60Hz tick would burn pointless CPU on mobile.
  let tickMs = $state(performance.now());
  let interval: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    interval = setInterval(() => {
      tickMs = performance.now();
    }, 1000);
  });

  onDestroy(() => {
    if (interval) clearInterval(interval);
  });

  const elapsedMs = $derived.by((): number => {
    if (frozenElapsedMs !== null && frozenElapsedMs !== undefined) return frozenElapsedMs;
    if (startedAtMs === null) return 0;
    return Math.max(0, tickMs - startedAtMs);
  });
</script>

<div class="quiz-hud" aria-live="polite">
  <div class="quiz-hud__timer" aria-label="Elapsed time">
    {formatHms(elapsedMs)}
  </div>
  <div class="quiz-hud__progress" aria-label="Quiz progress">
    {resolved}<span class="quiz-hud__progress-divider">/</span>{total}
  </div>
  <button
    class="quiz-hud__restart"
    type="button"
    onclick={onRestart}
    aria-label="Restart quiz from the beginning"
    title="Restart from the beginning"
  >
    Restart
  </button>
</div>
